import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import TextArea from "@/common/components/TextArea";
import { lastReviewSchema, LastReviewValidationSchema } from "@/common/schemas/lastReview.schema";
import { FormTransformers } from "@/common/utils/formTransformers";
import { SubmitHandler, useForm } from "react-hook-form";
import { useMaintenanceExecutionObjectiveDisapprove } from "../hooks/useMaintenanceExecutionObjectiveDisapprove";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

interface MaintenanceExecutionObjectiveDisapproveProps {
  objectiveId: string;
  onDisapprove?: VoidFunction;
}

const MaintenanceExecutionObjectiveDisapprove = ({
  objectiveId,
  onDisapprove,
}: MaintenanceExecutionObjectiveDisapproveProps) => {
  const { forwardingId } = useParams();

  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: {
      errors,
    },
  } = useForm<LastReviewValidationSchema>({
    resolver: zodResolver(lastReviewSchema),
    defaultValues: {
      state: 'disapproved',
    },
  });

  const {
    mutate: disapprove,
    isPending: disapproveIsPending,
  } = useMaintenanceExecutionObjectiveDisapprove({
    objectiveId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceForwarding', forwardingId],
        });

        setIsOpen(false);

        onDisapprove?.();
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      }
    }
  });

  const onSubmit: SubmitHandler<LastReviewValidationSchema> = (data) => disapprove(data);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="yellow"
            operationKlassToCheckPermission="Admin::Maintenance::ExecutionObjectives::Update"
            className="w-[120px]"
          >
            Declinar
          </Button>
        )}
        title="Reprovar diagnóstico"
        useCancellableFooter
        cancelButtonClassName="bg-white-100"
        confirmButtonClassName="bg-primary-blue"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={disapproveIsPending}
        bodyClassName="text-white-800"
        body={(
          <div className="flex flex-col gap-4">
            <p className="text-base font-semibold">
              Esse objeto será reprovado e após conclusão será devolvido para que o Estabelecimento realize correção
            </p>

            <TextArea
              label="Mensagem da vistoria"
              markAsRequired
              rows={4}
              placeholder="Escreva aqui sua justificativa"
              {...register('description')}
              errorMessage={errors.description?.message}
            />
          </div>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default MaintenanceExecutionObjectiveDisapprove;
